import React, { useEffect, useState } from 'react';
import { CalendarCheck, Copy, Check, Link2, ChevronDown, Loader2 } from 'lucide-react';
import {
  bookingIntegrationsService,
  bookingConnectService,
  PROVIDER_META,
  type BookingIntegration,
  type BookingProvider,
  type BookingEventType,
} from 'api/services/bookingIntegrations.service';

/**
 * Connecting Calendly or Cal.com.
 *
 * <h3>One token, one button</h3>
 * The first version of this page asked for two things in two places: paste our
 * URL into their dashboard, paste their signing secret into ours. That is the
 * step people get wrong and it fails SILENTLY — a mistyped secret refuses every
 * delivery, and the failures appear only in the vendor's dashboard, which nobody
 * here is watching.
 *
 * <p>With an API token the platform registers the webhook itself and generates
 * the secret, so neither is typed. The manual path is kept below a disclosure
 * for the case the token cannot be had — an enterprise Calendly where only an
 * owner can mint one.
 *
 * <h3>Test before Connect, and show whose account it is</h3>
 * A token pasted from the wrong login authenticates perfectly and would
 * subscribe somebody else's diary. Naming the account is the only step that
 * catches that, and it happens before anything is written.
 */
const BookingIntegrations: React.FC = () => {
  const [rows, setRows] = useState<BookingIntegration[]>([]);
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [events, setEvents] = useState<Record<string, BookingEventType[]>>({});
  const [manual, setManual] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [result, setResult] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const list = await bookingIntegrationsService.list();
    setRows(list);
    // Only for the ones already connected — asking the vendor about an account
    // we have no token for is a round trip that can only return nothing.
    for (const r of list.filter((x) => x.hasToken)) {
      bookingConnectService.eventTypes(r.provider)
        .then((e) => setEvents((s) => ({ ...s, [r.provider]: e })));
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await load();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const say = (provider: string, ok: boolean, text: string) =>
    setResult((s) => ({ ...s, [provider]: { ok, text } }));

  const test = async (provider: BookingProvider) => {
    setBusy(provider + ':test');
    const res = await bookingConnectService.test(provider, tokens[provider] || undefined);
    setBusy(null);
    say(provider, res.ok,
        res.ok ? `Connected as ${res.account || 'that account'}.`
               : res.reason || 'The token was not accepted.');
    if (res.ok) load();
  };

  const connect = async (provider: BookingProvider) => {
    setBusy(provider + ':connect');
    const res = await bookingConnectService.connect(provider, tokens[provider] || undefined);
    setBusy(null);
    say(provider, res.ok,
        res.ok ? `Connected as ${res.account}. The webhook is registered and bookings are on.`
               : res.reason || 'Could not register the webhook.');
    if (res.ok) {
      setTokens((s) => ({ ...s, [provider]: '' }));
      load();
    }
  };

  const saveManual = async (provider: BookingProvider, patch: { enabled?: boolean } = {}) => {
    setBusy(provider + ':save');
    const res = await bookingIntegrationsService.save(provider, {
      ...patch,
      signingSecret: secrets[provider] || undefined,
    });
    setBusy(null);
    if (res.ok) {
      setSecrets((s) => ({ ...s, [provider]: '' }));
      say(provider, true, 'Saved.');
      load();
    } else {
      say(provider, false, res.error);
    }
  };

  const copy = async (provider: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(provider);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      say(provider, false, 'Could not copy — select the URL and copy it by hand.');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>;
  }

  return (
    <div className="p-6 max-w-4xl space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarCheck className="w-5 h-5" /> Meeting bookings
        </h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          When somebody books, moves or cancels a meeting it is recorded against the lead and
          appears under Bookings.
        </p>
      </div>

      {rows.map((row) => {
        const meta = PROVIDER_META[row.provider];
        const note = result[row.provider];
        const types = events[row.provider] || [];
        return (
          <div key={row.provider} className="tl-card space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 dark:text-white">{meta.label}</p>
                <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{meta.help}</p>
                {row.account && (
                  <p className="text-[12px] mt-1 text-emerald-700 dark:text-emerald-400">
                    Connected as {row.account}
                    {row.verifiedAt && ` · webhook registered ${row.verifiedAt.slice(0, 10)}`}
                  </p>
                )}
              </div>
              {row.enabled && (
                <span className="tl-pill tl-pill-success shrink-0">On</span>
              )}
            </div>

            {/* The one-token path. */}
            <div>
              <label className="block text-[12px] mb-1" style={{ color: 'var(--text-muted)' }}>
                {meta.label} API token
              </label>
              <input
                type="password"
                className="tl-input w-full"
                placeholder={row.hasToken ? 'A token is stored — type to replace it' : 'Paste the token'}
                value={tokens[row.provider] || ''}
                onChange={(e) => setTokens((s) => ({ ...s, [row.provider]: e.target.value }))}
              />
              <p className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                Write-only: it is never shown again. With it we register the webhook and generate
                the signing secret for you — neither has to be pasted anywhere.
              </p>

              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button onClick={() => test(row.provider)}
                        disabled={busy !== null || (!row.hasToken && !tokens[row.provider])}
                        className="tl-btn tl-btn-neutral tl-btn-sm disabled:opacity-50">
                  {busy === row.provider + ':test'
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Test connection
                </button>
                <button onClick={() => connect(row.provider)}
                        disabled={busy !== null || (!row.hasToken && !tokens[row.provider])}
                        className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
                  {busy === row.provider + ':connect'
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                  {row.enabled ? 'Reconnect' : 'Connect'}
                </button>
              </div>
            </div>

            {note && (
              <p className="text-sm"
                 style={{ color: note.ok ? 'var(--text-muted)' : 'var(--danger-text)' }}>
                {note.text}
              </p>
            )}

            {/* What they are offering to book — proof the token reaches real data. */}
            {types.length > 0 && (
              <div>
                <p className="text-[12px] mb-1" style={{ color: 'var(--text-muted)' }}>
                  Bookable from this account
                </p>
                <ul className="space-y-1">
                  {types.slice(0, 6).map((t) => (
                    <li key={t.url || t.name} className="text-[12.5px] flex items-baseline gap-2 min-w-0">
                      <span className="text-gray-900 dark:text-white">{t.name}</span>
                      <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>
                        {t.minutes} min
                      </span>
                      <span className="truncate" style={{ color: 'var(--text-muted)' }}>{t.url}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Kept for the case the token cannot be had — an enterprise account
                where only an owner can mint one. Folded away, because it is the
                path that goes wrong. */}
            <div>
              <button type="button"
                      onClick={() => setManual((s) => ({ ...s, [row.provider]: !s[row.provider] }))}
                      className="text-[12px] inline-flex items-center gap-1 hover:underline"
                      style={{ color: 'var(--text-muted)' }}>
                <ChevronDown className={`w-3.5 h-3.5 transition ${manual[row.provider] ? 'rotate-180' : ''}`} />
                Set it up by hand instead
              </button>

              {manual[row.provider] && (
                <div className="mt-3 space-y-3 pl-4 border-l" style={{ borderColor: 'var(--border)' }}>
                  <div>
                    <p className="text-[12px] mb-1" style={{ color: 'var(--text-muted)' }}>
                      1 · Paste this into {meta.label}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 min-w-0 text-[12px] px-2 py-1.5 rounded border overflow-x-auto whitespace-nowrap"
                            style={{ borderColor: 'var(--border)', background: 'var(--surface-raised)' }}>
                        {row.webhookUrl}
                      </code>
                      <button type="button" onClick={() => copy(row.provider, row.webhookUrl)}
                              className="tl-btn tl-btn-neutral tl-btn-sm shrink-0">
                        {copied === row.provider
                          ? <><Check className="w-3.5 h-3.5" /> Copied</>
                          : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-[12px] mb-1" style={{ color: 'var(--text-muted)' }}>
                      2 · Paste their signing secret here
                    </p>
                    <input
                      type="password"
                      className="tl-input w-full"
                      placeholder={row.hasSecret ? 'A secret is stored — type to replace it' : 'Signing secret'}
                      value={secrets[row.provider] || ''}
                      onChange={(e) => setSecrets((s) => ({ ...s, [row.provider]: e.target.value }))}
                    />
                    <p className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      Checked against the <code>{meta.header}</code> header on every delivery.
                      Leaving this blank keeps the stored one.
                    </p>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => saveManual(row.provider)} disabled={busy !== null}
                            className="tl-btn tl-btn-neutral tl-btn-sm disabled:opacity-50">
                      Save
                    </button>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={row.enabled}
                             onChange={(e) => saveManual(row.provider, { enabled: e.target.checked })} />
                      <span className="text-gray-900 dark:text-white">Enabled</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BookingIntegrations;
